/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { IOutsourced } from "../../../../interfaces/outsourcedes";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import useMounted from "../../../../hooks/mounted";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  listOutsourcedes,
  showOutsourced,
  createOutsourced,
  updateOutsourced,
  reactivateOutsourced,
  deleteOutsourced,
} from "@/services";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const DEFAULT_MAINTENANCE: IOutsourced = {
  name: "",
  corporateReason: "",
  document: "",
  address: "",
  cep: "",
  city: "",
  state: "",
  location: { type: 'Point', coordinates: [0, 0] },
  coveredStates: [],
  coveredScopes: [],
  coveredServices: [],
  phone: "",
  email: "",
  statusEhs: true,
};

export const useOutsourcedes = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { idOutsourced: id } = useParams();
  const location = useLocation();
  const [outsourcedes, setOutsourcedes] = useState<IOutsourced[]>([]);
  const [outsourced, setOutsourced] = useState<IOutsourced>(DEFAULT_MAINTENANCE);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: defaultLimit || 10 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [enableChangePositionMap, setEnableChangePositionMap] = useState<boolean>(false);
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/outsourcedes" });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOutsourced = (
    key: string,
    value: any,
    custom?: (v: any) => any,
  ) => {
    setOutsourced((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = ({
    page = 1,
    limit,
    sort,
  }: {
    page?: number;
    limit?: number;
    sort?: any;
  }) => {
    setLoading(true);
    listOutsourcedes({ includes: [...includes], filter, page, limit, sort })
      .then(
        async ({
          outsourcedes: listOutsourcedes,
          limit,
          currentPage,
          totalItems,
        }) => {
          setOutsourcedes(listOutsourcedes);

          setLimit(Number(limit));
          setTotalItems(Number(totalItems));
        },
      )
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listOutsourcedes, key: "outsourcedes", includes, filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showOutsourced({ id })
      .then(({ outsourced: outsourcedesShow }) => {
        setOutsourced(outsourcedesShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createOutsourced({ outsourced })
      .then(({ outsourced: outsourcedCreate }) => {
        toast.success(`Terceirizada criado com sucesso!`);
        setOutsourced(outsourcedCreate);
        goEdit(outsourcedCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateOutsourced({ id: outsourced.id!, outsourced })
      .then(({ outsourced: outsourcedUpdate }) => {
        toast.success(`Terceirizada atualizado com sucesso!`);
        goEdit(outsourcedUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteOutsourced({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Terceirizada",
        plural: "terceirizadas",
        gender: "f",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, sort });
    }
  };

  const activate = () => {
    setLoading(true);
    reactivateOutsourced({ id: outsourced.id! })
      .then(({ outsourced: outsourcedActivated }) => {
        toast.success(`Terceirizada reativada com sucesso!`);
        setOutsourced(outsourcedActivated);
      })
      .finally(() => setLoading(false));
  };

  useMounted(() => {
    if (!disabledGetAll) getAll({ page, limit, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    const isPage = location.pathname.includes("/towermanagement/outsourcedes");
    if (id && isPage && !disabledShowId) {
      if (id !== "new") get({ id: id });
    }
  }, []);
  return {
    outsourcedes,
    outsourced,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    deleteMany,
    goBack,
    goAdd,
    handleOutsourced,
    create,
    id,
    update,
    activate,
    enableChangePositionMap,
    setEnableChangePositionMap,
    totalItems,
    search,
    handleFilter,
    handleSort,
  };
};
